import axios from 'axios';
import { config } from '../config';
import { ApiResponse, Device, Site, FilterParams, DeviceSummary } from '../types/api';

const api = axios.create({
    baseURL: config.API_BASE_URL,
    headers: {
        'X-API-Key': config.API_KEY
    }
});

// Add error handling helper
const handleApiResponse = <T>(response: { data: ApiResponse<T> }) => {
    if (response.data.status === 'error') {
        throw new Error(response.data.data.error || response.data.message || 'An unknown error occurred');
    }
    return response.data;
};

const createFilterExpression = (key: string, value: string) => {
    // Use LIKE operator for text search fields
    if (key === 'NetworkComputerName' || key === 'SystemSerialNumber' || key === 'OsLastLoggedOnUser') {
        return `\`${key}\` LIKE '%${value}%'`;
    }
    // Use exact match for other fields
    return `\`${key}\`='${value}'`;
};

export const fetchDevices = async (page: number = 1, pageSize: number = 10, filters?: FilterParams) => {
    let queryParams = `pageSize=${pageSize}&page=${page}`;
    
    if (filters) {
        const activeFilters = Object.entries(filters)
            .filter(([_, value]) => value)
            .map(([key, value]) => createFilterExpression(key, value as string))
            .join(' AND ');
            
        if (activeFilters) {
            const encodedFilters = encodeURIComponent(activeFilters);
            queryParams += `&filter=${encodedFilters}`;
        }
    }
    
    const response = await api.get<ApiResponse<Device>>(`/devices?${queryParams}`);
    return handleApiResponse(response);
};

export const fetchAllDevicesForExport = async (filters?: FilterParams) => {
    let queryParams = '';  // Set pageSize to 0 to get all records
    
    if (filters) {
        const activeFilters = Object.entries(filters)
            .filter(([_, value]) => value)
            .map(([key, value]) => createFilterExpression(key, value as string))
            .join(' AND ');
            
        if (activeFilters) {
            const encodedFilters = encodeURIComponent(activeFilters);
            queryParams += `&filter=${encodedFilters}`;
        }
    }
    
    const response = await api.get<ApiResponse<Device>>(`/devices?${queryParams}`);
    return handleApiResponse(response);
};

export const fetchSites = async () => {
    const response = await api.get<ApiResponse<Site>>('/sites');
    return handleApiResponse(response);
};

export const fetchDeviceSummary = async () => {
    const response = await api.get<ApiResponse<DeviceSummary>>('/devicesummary');
    return handleApiResponse(response);
}; 